import { Injectable, signal } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import {
  Common,
  NOTIFICATION_WS_CONFIG,
  NotificationWebSocketConnectParams,
  WS_CONNECTION_STATE,
  WS_MESSAGE_TYPE,
  WsConnectionState,
} from '@techextensor/tab-core-utility';
import { TabSdk } from '@techextensor/tab-sdk';

/** Subprotocol marker the notification service reads the access token from (`access_token, <jwt>`). */
export const NOTIFICATION_WS_TOKEN_SUBPROTOCOL = 'access_token';

/**
 * Drop-in replacement for tab-core-utility's `NotificationWebSocketService` (the bell's transport),
 * registered in app.config via `{ provide: NotificationWebSocketService, useClass: ... }`.
 *
 * Identical behaviour and public surface (connect / disconnect / send / messages$ /
 * connectionState / isConnected), plus: every handshake carries the CURRENT access token as the
 * WebSocket subprotocol pair `access_token, <jwt>` (browsers cannot set an Authorization header on
 * a WebSocket). The token is read from `TabSdk.store.token` at each (re)connect, so a reconnect after
 * a token refresh always presents the fresh token.
 *
 * The notification service binds the socket to the token's `sub` (WebSocketAuth:Mode=Enforce), so
 * the `userId` query parameter can no longer be spoofed. The server echoes the subprotocol — deploy
 * the notification service BEFORE this FE change (a server that does not echo it fails the handshake).
 *
 * TODO: move into @techextensor/tab-core-utility's NotificationWebSocketService (its source is not in
 * this workspace) and delete this override.
 */
@Injectable()
export class AuthedNotificationWebSocketService {
  private socket: WebSocketSubject<any> | null = null;
  private socketSub: Subscription | null = null;
  private connectingPromise: Promise<void> | null = null;
  private explicitlyDisconnected = false;
  private lastParams: NotificationWebSocketConnectParams | null = null;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  readonly connectionState = signal<WsConnectionState>(WS_CONNECTION_STATE.DISCONNECTED);
  readonly messages$ = new Subject<any>();

  async connect(params: NotificationWebSocketConnectParams): Promise<void> {
    this.explicitlyDisconnected = false;
    this.lastParams = params;
    if (this.connectionState() === WS_CONNECTION_STATE.CONNECTED) {
      return;
    }
    if (this.connectingPromise) {
      return this.raceWithTimeout(this.connectingPromise);
    }
    this.teardownSocket();
    this.connectingPromise = this.doConnect(params);
    this.connectingPromise.finally(() => {
      this.connectingPromise = null;
    });
    return this.raceWithTimeout(this.connectingPromise);
  }

  send(message: Record<string, any>): void {
    if (!this.socket || this.connectionState() !== WS_CONNECTION_STATE.CONNECTED) {
      return;
    }
    try {
      this.socket.next(message);
    } catch (error) {
      console.error('[NotificationWS] Failed to send message:', error);
    }
  }

  async disconnect(): Promise<void> {
    this.explicitlyDisconnected = true;
    this.connectingPromise = null;
    this.lastParams = null;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.teardownSocket();
    this.reconnectAttempt = 0;
    this.connectionState.set(WS_CONNECTION_STATE.CLOSED);
  }

  isConnected(): boolean {
    return this.connectionState() === WS_CONNECTION_STATE.CONNECTED;
  }

  // ----------- internal -----------

  /** Current raw JWT (no "Bearer " prefix), read fresh on every handshake. */
  private currentAccessToken(): string {
    const stored = (TabSdk.store.token ?? '').toString().trim();
    return stored.replace(/^Bearer\s+/i, '');
  }

  private raceWithTimeout(p: Promise<void>): Promise<void> {
    return Promise.race([
      p,
      new Promise<void>(resolve => setTimeout(resolve, NOTIFICATION_WS_CONFIG.CONNECT_TIMEOUT_MS)),
    ]);
  }

  private teardownSocket(): void {
    if (this.socket) {
      try {
        this.socket.complete();
      } catch { /* ignore */ }
      this.socket = null;
    }
    this.socketSub?.unsubscribe();
    this.socketSub = null;
  }

  private doConnect(params: NotificationWebSocketConnectParams): Promise<void> {
    const baseUrl = Common.notificationWebSocketUrl;
    if (!baseUrl) {
      this.connectionState.set(WS_CONNECTION_STATE.DISCONNECTED);
      return Promise.resolve();
    }
    if (!params.userId) {
      console.warn('[NotificationWS] No userId provided — skipping connect');
      this.connectionState.set(WS_CONNECTION_STATE.FAILED);
      return Promise.resolve();
    }

    const url = `${baseUrl}?userId=${encodeURIComponent(params.userId)}` +
      (params.appId ? `&appId=${encodeURIComponent(params.appId)}` : '') +
      (params.tenantId ? `&tenantId=${encodeURIComponent(params.tenantId)}` : '');

    // JWTs are base64url + '.', all valid subprotocol token characters.
    const token = this.currentAccessToken();
    const protocol = token ? [NOTIFICATION_WS_TOKEN_SUBPROTOCOL, token] : undefined;

    this.connectionState.set(WS_CONNECTION_STATE.CONNECTING);
    return new Promise<void>(resolve => {
      this.socket = webSocket({
        url,
        protocol,
        openObserver: {
          next: () => {
            this.connectionState.set(WS_CONNECTION_STATE.CONNECTED);
            this.reconnectAttempt = 0;
            resolve();
          },
        },
        closeObserver: {
          next: () => {
            if (this.connectionState() !== WS_CONNECTION_STATE.CLOSED) {
              this.connectionState.set(WS_CONNECTION_STATE.DISCONNECTED);
            }
            // Reconnect re-reads the token, so a refreshed token is picked up automatically.
            this.scheduleReconnect();
            resolve();
          },
        },
      });
      this.socketSub = this.socket.subscribe({
        next: msg => this.handleIncoming(msg),
        error: err => {
          console.error('[NotificationWS] Socket error:', err);
          this.connectionState.set(WS_CONNECTION_STATE.FAILED);
          this.scheduleReconnect();
          resolve();
        },
      });
    });
  }

  private handleIncoming(message: any): void {
    if (!message || typeof message !== 'object') return;
    // Heartbeat: server pings every ~30s; we MUST reply or the server closes the socket.
    if (message.type === WS_MESSAGE_TYPE.HEARTBEAT) {
      this.send({ type: WS_MESSAGE_TYPE.HEARTBEAT });
      return;
    }
    if (message.type === WS_MESSAGE_TYPE.CONNECTION_ESTABLISHED) {
      this.connectionState.set(WS_CONNECTION_STATE.CONNECTED);
    }
    if (message.type === WS_MESSAGE_TYPE.ERROR) {
      console.warn('[NotificationWS] Server error:', message.error);
    }
    this.messages$.next(message);
  }

  private scheduleReconnect(): void {
    if (this.explicitlyDisconnected || this.reconnectTimer || !this.lastParams) return;
    const delays = NOTIFICATION_WS_CONFIG.RECONNECT_DELAYS_MS;
    const delay = delays[Math.min(this.reconnectAttempt, delays.length - 1)];
    this.reconnectAttempt++;
    this.connectionState.set(WS_CONNECTION_STATE.RECONNECTING);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.explicitlyDisconnected && this.lastParams) {
        this.connect(this.lastParams);
      }
    }, delay);
  }
}
